import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Button, SettingsGroup, SettingsItemAutocomplete } from '@elipssolution/harfang';
import { styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import SettingsDialogPage from '../../../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../../../hooks/useSettingsDialog';
import { CustomerFileType } from '../../../../../../types/customerFile';
import { SUCCESS_ANIMATION_DELAY } from '../../../../../../utils/statusAnimationDelay';
import {
	FETCH_COUNTERPART_ACCOUNTS_BY_CUSTOMER_FILE,
	FetchCounterpartAccountsByCustomerFileType,
} from '../../../../api/account';
import {
	CREATE_CUSTOMER_FILE_CHECK_REMITTANCE_SETTING_COUNTERPART_ACCOUNT,
	CreateCustomerFileCheckRemittanceSettingCounterpartAccountType,
	FETCH_CUSTOMER_FILE_CHECK_REMITTANCE_SETTING_COUNTERPART_ACCOUNT,
	FetchCustomerFileCheckRemittanceSettingCounterpartAccountType,
} from '../../../../api/settingCheckRemittanceCounterpartAccount';
import { AccountType } from '../../../../types/account';

const ActionWrapper = styled('div')({
	display: 'flex',
	flexDirection: 'row-reverse',
	justifyContent: 'space-between',
});

const ErrorWrapper = styled('div')(({ theme: { palette, shape } }) => ({
	height: 36,
	width: '50%',

	display: 'grid',
	placeItems: 'center',

	color: palette.error.main,
	backgroundColor: `${palette.error.main}1A`,
	borderRadius: shape.borderRadius,
}));

type FormFieldType = {
	counterpartAccount?: {
		code: AccountType['code'];
	};
};

type CheckRemittanceCounterpartProps = {
	customerFileId?: CustomerFileType['id'];
};

const CheckRemittanceCounterpartAccount = ({ customerFileId }: CheckRemittanceCounterpartProps) => {
	const { onHasDirtyFormChange } = useSettingsDialog();

	const {
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		setValue,
		setError: setFormError,
		clearErrors,
		reset,
	} = useForm<FormFieldType>();

	const [error, setError] = useState<string>();

	const isFormValid = useMemo(() => isDirty && isValid, [isDirty, isValid]);

	const [fetchCounterpartAccountsByCustomerFile] = useLazyQuery<FetchCounterpartAccountsByCustomerFileType>(
		FETCH_COUNTERPART_ACCOUNTS_BY_CUSTOMER_FILE,
	);

	const counterpartAccountCodesDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
		): Promise<{
			count: number;
			items: { code: AccountType['code'] }[];
		}> => {
			const { data, error: queryError } = await fetchCounterpartAccountsByCustomerFile({
				variables: {
					page: {
						limit,
						offset,
					},
					customerFileId,
					search,
				},
			});

			if (queryError) {
				throw queryError;
			}

			const {
				quickentry_counterpartAccounts: { count = 0, items = [] },
			} = data ?? {
				quickentry_counterpartAccounts: {},
			};

			return {
				count,
				items: items.map(({ code }) => ({ code })),
			};
		},
		[customerFileId, fetchCounterpartAccountsByCustomerFile],
	);

	const { data: fetchedCustomerFileCheckRemittanceSettingCounterpartAccountData } =
		useQuery<FetchCustomerFileCheckRemittanceSettingCounterpartAccountType>(
			FETCH_CUSTOMER_FILE_CHECK_REMITTANCE_SETTING_COUNTERPART_ACCOUNT,
			{
				variables: {
					id: customerFileId,
				},
				onCompleted: ({
					quickentry_settingCustomerFileCheckRemittanceCounterpartAccount: {
						data: { accountCode, inheritedAccountCode },
						error: errorValue,
					},
				}) => {
					if (accountCode) {
						setValue('counterpartAccount', { code: accountCode ?? inheritedAccountCode });
					}

					if (errorValue) {
						setFormError('counterpartAccount', { message: `${errorValue.fieldValue} n'existe pas dans le dossier` });
					}
				},
			},
		);

	const { quickentry_settingCustomerFileCheckRemittanceCounterpartAccount } =
		fetchedCustomerFileCheckRemittanceSettingCounterpartAccountData || {};
	const { data: settingCustomerFileCheckRemittanceCounterpartAccountData } =
		quickentry_settingCustomerFileCheckRemittanceCounterpartAccount || {};
	const { inheritedAccountCode } = settingCustomerFileCheckRemittanceCounterpartAccountData || {};

	const [
		createCheckRemittanceCounterpartAccount,
		{ data: createCheckRemittanceCounterpartAccountData, reset: resetCreateCheckRemittanceCounterpartAccount },
	] = useMutation<CreateCustomerFileCheckRemittanceSettingCounterpartAccountType>(
		CREATE_CUSTOMER_FILE_CHECK_REMITTANCE_SETTING_COUNTERPART_ACCOUNT,
		{
			onError: (mutationError) => {
				setError(mutationError.message);
			},
		},
	);

	const onSubmit = useCallback(
		async ({ counterpartAccount: formCounterpartAccount }: FormFieldType) => {
			await createCheckRemittanceCounterpartAccount({
				variables: {
					createSettingCustomerFileCheckRemittanceCounterpartAccountInput: {
						accountCode: formCounterpartAccount?.code,
						customerFileId,
					},
				},
			});

			reset({ counterpartAccount: formCounterpartAccount });

			setTimeout(() => {
				resetCreateCheckRemittanceCounterpartAccount();
			}, SUCCESS_ANIMATION_DELAY);
		},
		[createCheckRemittanceCounterpartAccount, customerFileId, reset, resetCreateCheckRemittanceCounterpartAccount],
	);

	useEffect(() => onHasDirtyFormChange(isDirty), [isDirty, onHasDirtyFormChange]);

	return (
		<SettingsDialogPage title="Contrepartie de remise de chèques">
			<SettingsGroup>
				<Controller
					control={control}
					name="counterpartAccount"
					render={({ field: { onChange, ...field }, fieldState: { error: errorField } }) => {
						const handleCounterpartAccountCodeChange = (
							counterpartAccountCode?: { code: AccountType['code'] } | null,
						) => {
							onChange?.(counterpartAccountCode);
							clearErrors('counterpartAccount');
						};

						return (
							<SettingsItemAutocomplete<{ code: AccountType['code'] }>
								{...field}
								dataKey="code"
								onChange={handleCounterpartAccountCodeChange}
								dataSource={counterpartAccountCodesDataSource}
								getOptionLabel={({ code }) => `${code}`}
								label="Compte de contrepartie"
								invalid={!!errorField}
								helperText={errorField ? "Le compte n'existe pas au dossier" : ''}
								inheritedValue={inheritedAccountCode ? { code: inheritedAccountCode } : undefined}
								disableClearable
							/>
						);
					}}
				/>
			</SettingsGroup>

			<ActionWrapper>
				<Button disabled={!isFormValid} onClick={handleSubmit(onSubmit)} variant="contained">
					{createCheckRemittanceCounterpartAccountData ? 'Contrepartie validée' : 'Valider'}
				</Button>

				{error && <ErrorWrapper>{error}</ErrorWrapper>}
			</ActionWrapper>
		</SettingsDialogPage>
	);
};

export default CheckRemittanceCounterpartAccount;
